import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, MapPin, Phone, Clock, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONTACT as T } from "@/constants/testIds";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "Quote Request",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/inquiries", form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", subject: "Quote Request", message: "" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={T.root}
      className="py-28 md:py-40 bg-[#0a0a0a] border-t border-white/5"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <p className="eyebrow mb-6">Get in Touch — 07 / 07</p>
            <h2 className="font-serif text-5xl md:text-7xl font-light text-white leading-[0.95]">
              Drop off <span className="italic text-neutral-400">& reveal.</span>
            </h2>
            <p className="mt-6 text-neutral-400 max-w-md text-base md:text-lg leading-relaxed">
              Bring your vehicle to our Paralowie studio. Get a quote, ask about
              ceramic packages, or send through a picture of your vehicle.
            </p>

            <ul className="mt-12 space-y-6">
              <li className="flex gap-4">
                <MapPin className="text-neutral-500 shrink-0 mt-1" size={18} strokeWidth={1.4} />
                <div>
                  <p className="eyebrow mb-1">Studio</p>
                  <p className="text-white">39 Reynolds Drive, Paralowie SA 5108</p>
                </div>
              </li>
              <li className="flex gap-4">
                <Phone className="text-neutral-500 shrink-0 mt-1" size={18} strokeWidth={1.4} />
                <div>
                  <p className="eyebrow mb-1">Direct Line</p>
                  <a
                    href="tel:0429052189"
                    className="text-white link-editorial"
                  >
                    0429 052 189
                  </a>
                </div>
              </li>
              <li className="flex gap-4">
                <Clock className="text-neutral-500 shrink-0 mt-1" size={18} strokeWidth={1.4} />
                <div>
                  <p className="eyebrow mb-1">Hours</p>
                  <p className="text-white">7 Days · 08:30 — 18:30</p>
                </div>
              </li>
              <li className="flex gap-4">
                <Mail className="text-neutral-500 shrink-0 mt-1" size={18} strokeWidth={1.4} />
                <div>
                  <p className="eyebrow mb-1">Social</p>
                  <div className="flex gap-4 text-white">
                    <a
                      href="https://www.instagram.com/seventh_car_detailing"
                      target="_blank"
                      rel="noreferrer"
                      className="link-editorial"
                    >
                      Instagram
                    </a>
                    <a
                      href="https://www.facebook.com/seven7hcardetailing"
                      target="_blank"
                      rel="noreferrer"
                      className="link-editorial"
                    >
                      Facebook
                    </a>
                    <a
                      href="https://www.youtube.com/@SEVEN7HCARDEATILING"
                      target="_blank"
                      rel="noreferrer"
                      className="link-editorial"
                    >
                      YouTube
                    </a>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            {success ? (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                data-testid={T.success}
                className="border border-white/10 bg-white/[0.02] p-10 md:p-14"
              >
                <CheckCircle2 className="text-white mb-6" size={40} strokeWidth={1} />
                <h3 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-3">
                  Message <span className="italic text-neutral-400">received.</span>
                </h3>
                <p className="text-neutral-400 max-w-md">
                  We'll get back to you shortly — usually within the same day.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-8 font-mono text-[11px] tracking-[0.24em] uppercase text-neutral-400 hover:text-white"
                >
                  Send another
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-8 border-t border-white/10 pt-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="eyebrow block mb-3">Name</label>
                    <input
                      data-testid={T.name}
                      className="input-minimal"
                      value={form.name}
                      required
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-3">Phone</label>
                    <input
                      data-testid={T.phone}
                      className="input-minimal"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="eyebrow block mb-3">Email</label>
                    <input
                      data-testid={T.email}
                      type="email"
                      className="input-minimal"
                      value={form.email}
                      required
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="eyebrow block mb-3">Subject</label>
                    <input
                      data-testid={T.subject}
                      className="input-minimal"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="eyebrow block mb-3">Message</label>
                    <textarea
                      data-testid={T.message}
                      className="input-minimal resize-none"
                      rows={4}
                      value={form.message}
                      required
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-red-400 text-sm font-mono">{error}</p>
                )}

                <div className="pt-6 border-t border-white/10">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid={T.submit}
                    className="btn-primary"
                  >
                    {submitting ? "Sending…" : "Send Message"} <ArrowRight size={14} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
